<template>
  <div>
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-3">
        <div class="box bg-white border border-gray-200 rounded-lg p-5 h-full flex flex-col gap-3">
          <StatCard
            v-for="s in BasicStats"
            :key="s.label"
            :label="s.label"
            :value="s.value"
            :unit="s.unit"
            align="left"
          >
            <template #iconRight>
              <div class="w-6 h-6">
                <component
                  :is="s.icon"
                  class="w-full h-full"
                />
              </div>
            </template>
          </StatCard>
        </div>
      </div>

      <div class="col-span-12 lg:col-span-4">
        <div class="box bg-white border border-gray-200 rounded-lg p-5 h-full flex flex-col" data-pdf-list>
          <SectionTitle title="ปัญหาเด่น" />
          <div v-if="!nationalTopProblems.length" class="flex items-center justify-center h-[400px]">
            <p class="text-sm text-gray-600">ไม่มีข้อมูล</p>
          </div>
          <ul class="divide-y divide-gray-100 flex-1" v-else>
            <li v-for="(p, indx) in nationalTopProblems" :key="p.rank" class="py-3 first:pt-0 last:pb-0">
              <p class="text-sm font-semibold" :class="indx == 0 ? 'text-theme-5' : 'text-dark'">#{{ p.rank }} {{ p.label }}</p>
              <p class="text-xs text-gray-600 mt-0.5">
                {{ p.pct }}% ของ{{scope}}ทั้งหมด ({{ formatCount(p.count) }} {{scope}})
              </p>
            </li>
          </ul>
        </div>
      </div>

      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="สรุปสภาพปัญหาแยกรายมิติ" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="ovNationalDimChart"
              type="bar"
              :data="nationalDimData"
              :options="nationalDimOptions"
              fill-height
              :unit="scope"
            />
          </div>
        </ChartCard>
      </div>
    </div>

    <SectionTitle title="ความมั่นคงทางเศรษฐกิจ" />
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        v-for="s in nationalEconomicStats"
        :key="s.label"
        :label="s.label"
        :value="s.value"
        :unit="s.unit"
        align="left"
      >
        <template #iconRight>
          <div class="w-6 h-6">
            <component
              :is="s.icon"
              class="w-full h-full"
            />
          </div>
        </template>
      </StatCard>
      
    </div>
    <div class="mt-4">
      <ChartCard title="รายได้เฉลี่ยต่อเดือน">
        <template v-if="area === 'ทั้งประเทศ'" #actions>
          <PillTabs :model-value="incomeTab" :tabs="incomeTabs" @update:model-value="incomeTab = $event" />
        </template>
        <div class="h-[450px]">
          <DashboardBarEChart
            chart-id="ovIncomeChart"
            type="bar"
            :data="incomeData"
            :options="dualAxisOptions"
            :plugins="[povertyLinePlugin]"
            min-width="520px"
            fill-height
            unit="บาท"
          />
        </div>
      </ChartCard>
    </div>

    <SectionTitle title="การศึกษาและทักษะที่จำเป็น" />
    <ChartCard title="การเข้าถึงการศึกษา">
      <template #actions>
        <PillTabs :model-value="eduTab" :tabs="eduTabs" @update:model-value="eduTab = $event" />
      </template>
      <div class="h-[450px]">
        <DashboardBarEChart
          chart-id="ovEduChart"
          type="bar"
          :data="eduData"
          :options="thresholdOptions"
          fill-height
          :unit="scope"
        />
      </div>
    </ChartCard>

    <SectionTitle title="สถานะทางสุขภาพ" />
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        v-for="s in nationalHealthStats"
        :key="s.label"
        :label="s.label"
        :value="s.value"
        :unit="s.unit"
        align="left"
      >
        <template #iconRight>
          <div class="w-6 h-6">
            <component
              :is="s.icon"
              class="w-full h-full"
            />
          </div>
        </template>
      </StatCard>
    </div>
    <div class="grid grid-cols-12 gap-6 mt-4">
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ผู้มีโรคประจำตัว">
          <template v-if="area === 'ทั้งประเทศ'" #actions>
            <PillTabs :model-value="patientTab" :tabs="patientTabs" @update:model-value="patientTab = $event" />
          </template>
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="ovNationalPatientChart"
              type="bar"
              :data="nationalPatientData"
              :options="groupBarChartOptions"
              min-width="520px"
              fill-height
              :unit="scope"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ผู้มีโรคประจำตัวแยกตามประเภท">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="ovNationalDiseaseChart"
              type="bar"
              :data="nationalDiseaseTypeData"
              :options="simpleBarChartOptions"
              min-width="520px"
              fill-height
              :unit="scope"
            />
          </div>
        </ChartCard>
      </div>
    </div>
    
    <SectionTitle title="คุณภาพชีวิตความเป็นอยู่" />
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        v-for="s in nationalQualityStats"
        :key="s.label"
        :label="s.label"
        :value="s.value"
        :unit="`${s.unit} (${s.percent})`"
        align="left"
      >
        <template #iconRight>
          <div class="w-6 h-6">
            <component
              :is="s.icon"
              class="w-full h-full"
            />
          </div>
        </template>
      </StatCard>
    </div>
    <div class="grid grid-cols-12 gap-6 mt-4">
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="สภาพของที่อยู่อาศัย">
          <div class="h-[450px]">
            <ChartWithLegend
              chart-id="ovNationalQualityDoughnut"
              :series="nationalQualityDoughnut.series"
              :labels="nationalQualityDoughnut.labels"
              :unit="scope"
              :isShowPercent="false"
              variant="plain"
              legendLayout="horizontal"
              :showTotal="false"
              :show-data-label="true"
              :palette="nationalQualityDoughnutPalette"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-8">
        <ChartCard title="ข้อมูลตามประเภท">
          <template #actions>
            <div class="flex items-center gap-2">
              <PillTabs v-if="area === 'ทั้งประเทศ'" :model-value="qualityGroupTab" :tabs="qualityGroupTabs" @update:model-value="qualityGroupTab = $event" />
            </div>
            <div class="flex items-center gap-2">
              <PillTabs :model-value="qualityTab" :tabs="qualityTabs" @update:model-value="qualityTab = $event" />
            </div>
          </template>
          <div class="flex flex-col gap-2">
            <div class="h-[450px]">
              <DashboardBarEChart
                chart-id="ovNationalQualityChart"
                type="bar"
                :data="nationalQualityBarData"
                :options="nationalQualityBarOptions"
                :min-width="nqChartWidth"
                fill-height
                :unit="scope"
              />
            </div>
          </div>
        </ChartCard>
      </div>
    </div>

    <SectionTitle title="การเข้าถึงบริการภาครัฐ" />
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="การเข้าถึงบริการ" class="h-full">
          <template v-if="area === 'ทั้งประเทศ'" #actions>
            <PillTabs :model-value="serviceTab" :tabs="serviceTabs" @update:model-value="serviceTab = $event" />
          </template>
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="ovServiceChart"
              type="bar"
              :data="serviceData"
              :options="groupBarChartOptions"
              min-width="520px"
              fill-height
              :unit="scope"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ข้อมูลการได้รับสวัสดิการภาครัฐ" class="h-full">
          <!-- <template #actions>
            <div class="w-44">
              <v-select
                v-model="welfareAreaSelected"
                :options="welfareAreaOptions"
                v-bind="welfareAreaLabel ? { label: welfareAreaLabel } : {}"
                :loading="welfareAreaLoading"
                :clearable="false"
                :placeholder="welfareAreaPlaceholder"
                :append-to-body="true"
              >
                <template #no-options="">
                  <p class="text-sm font-normal text-gray-500">ไม่พบข้อมูล</p>
                </template>
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"><ChevronDownIcon class="w-4 h-4" /></span>
                </template>
              </v-select>
            </div>
          </template> -->
          <div class="h-[420px]">
            <DashboardBarEChart
              chart-id="ovWelfareChart"
              type="bar"
              :data="welfareData"
              :options="simpleBarChartOptions"
              min-width="520px"
              fill-height
              :unit="scope"
            />
          </div>
        </ChartCard>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// :deep(#ovNationalQualityDoughnut) {
//   height: 100%;
// }
// :deep(#ovNationalQualityDoughnut > div:first-child) {
//   height: 100%;
// }
</style>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import ChartDataLabels from "chartjs-plugin-datalabels";
import StatCard from "../container/StatCard.vue";
import SectionTitle from "../container/SectionTitle.vue";
import ChartCard from "../container/ChartCard.vue";
import PillTabs from "../container/PillTabs.vue";
import ChartWithLegend from "../container/ChartWithLegend.vue";
import DashboardBarEChart from "../container/DashboardBarEChart.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";

const props = defineProps({
  area: { type: String, default: "ทั้งประเทศ" },
  scope: { type: String, default: "ครัวเรือน" },
});

const store = useStore();
const filterParams = computed(() => store.getters["dashboardOverviewFilter/getFilterForApi"]);
const authHeaders = () => ({
  Authorization: `Bearer ${store.getters["userAuth/getToken"]}`,
  "Content-Type": "application/json",
});
const apiGet = (path, extraParams = {}) =>
  axios.get(`${process.env.VUE_APP_BASE_API}${path}`, {
    headers: authHeaders(),
    params: { ...filterParams.value, ...extraParams },
  }).catch((err) => err);

const welfareFilterAddress = computed(
  () => store.getters["dashboardOverviewFilter/getAllFilter"]?.address || {}
);

const welfareLevel = computed(() => {
  const a = welfareFilterAddress.value;
  if (a.tumbol) return "village";
  if (a.amphur) return "tumbol";
  if (a.province) return "amphur";
  if (a.region) return "province";
  return "region";
});

const WELFARE_REGION_OPTIONS = [
  "ภาคเหนือ", "ภาคกลาง", "ภาคตะวันออกเฉียงเหนือ",
  "ภาคตะวันออก", "ภาคตะวันตก", "ภาคใต้",
];
const welfareAreaOptions = ref([]);
const welfareAreaLoading = ref(false);
const welfareAreaSelected = ref(null);

const welfareAreaLabel = computed(() => {
  const map = {
    region: null,
    province: "province_name",
    amphur: "amphur_name",
    tumbol: "tumbol_name",
    village: "village_name",
  };
  return map[welfareLevel.value];
});

const welfareAreaPlaceholder = computed(() => {
  const map = {
    region: "เลือกภาค",
    province: "เลือกจังหวัด",
    amphur: "เลือกอำเภอ",
    tumbol: "เลือกตำบล",
    village: "เลือกหมู่บ้าน",
  };
  return map[welfareLevel.value];
});

const loadWelfareOptions = async () => {
  const a = welfareFilterAddress.value;
  const level = welfareLevel.value;

  if (level === "region") {
    welfareAreaOptions.value = WELFARE_REGION_OPTIONS;
    welfareAreaSelected.value = WELFARE_REGION_OPTIONS[0];
    return;
  }

  welfareAreaLoading.value = true;
  const headers = {
    Authorization: `Bearer ${store.getters["userAuth/getToken"]}`,
    "Content-Type": "application/json; charset=utf-8",
  };

  let url = `${process.env.VUE_APP_BASE_API}refadms/`;
  if (level === "amphur") {
    url += `?prov=${a.province._id.province_ID}`;
  } else if (level === "tumbol") {
    url += `?prov=${a.province._id.province_ID}&amp=${a.amphur._id.amphur_ID}`;
  } else if (level === "village") {
    url += `?prov=${a.province._id.province_ID}&amp=${a.amphur._id.amphur_ID}&tam=${a.tumbol._id.tumbol_ID}`;
  }

  try {
    const res = await axios.get(url, { headers });
    if (res.status === 200) {
      welfareAreaOptions.value = res.data;
      welfareAreaSelected.value = res.data[0] ?? null;
    }
  } finally {
    welfareAreaLoading.value = false;
  }
};

watch(welfareLevel, () => {
  welfareAreaSelected.value = null;
  loadWelfareOptions();
}, { immediate: true });

const formatCount = (n) => n.toLocaleString("th-TH");

const formatPercent = (num, denom) => {
  const raw = (Number(num) / Number(denom)) * 100 || 0;
  return `${Number.isInteger(raw) ? raw : raw.toFixed(1)}%`;
};

const DIMENSION_DISPLAY = [
  { key: "รายได้", label: "รายได้", legendLabel: "รายได้", color: "#3A506A" },
  { key: "การเข้าถึงการศึกษา", label: ["การเข้าถึง", "การศึกษา"], legendLabel: "การเข้าถึงการศึกษา", color: "#3A506A" },
  { key: "สถานะทางสุขภาพ", label: ["สถานะทาง", "สุขภาพ"], legendLabel: "สถานะทางสุขภาพ (ผู้ป่วย และผู้พิการ)", color: "#3A506A" },
  { key: "คุณภาพชีวิตความเป็นอยู่", label: ["คุณภาพชีวิต", "ความเป็นอยู่"], legendLabel: "คุณภาพชีวิตความเป็นอยู่ (น้ำกิน ฯลฯ)", color: "#3A506A" },
  { key: "การเข้าถึงบริการภาครัฐ", label: ["การเข้าถึง", "บริการภาครัฐ"], legendLabel: "การเข้าถึงบริการภาครัฐ", color: "#3A506A" },
];

const fetchOverall = async () => {
  const res = await apiGet("dashboard/overall");
  if (res?.status === 200) {
    const data = res.data || {};
    BasicStats.value = [
      {label: "จำนวนครัวเรือน", unit: "ครัวเรือน", value: globalNumDecFixed(data.total_house || 0 , 2), icon: "HouseIcon"},
      {label: "จำนวนสมาชิก", unit: "คน", value: globalNumDecFixed(data.total_member || 0, 2), icon: "MemberIcon"},
      {label: "จำนวนเด็ก NEETs", unit: "คน", value: globalNumDecFixed(data.total_neet || 0, 2), icon: "EducationIcon"},
      {label: "จำนวนเด็กพิการ", unit: "คน", value: globalNumDecFixed(data.total_cwd || 0, 2), icon: "DisabilityNewIcon"}
    ];

    const topProblems = Array.isArray(data.top_problems) ? data.top_problems : [];
    nationalTopProblems.value = topProblems.map((item) => ({
      rank: item.rank,
      label: item.label,
      pct: Number(item.percent || 0),
      count: globalNumDecFixed(item.count || 0),
    })).filter((item) => item.count > 0);

    const dims = Array.isArray(data.dimension_summary) ? data.dimension_summary : [];
    dimensionSummary.value = DIMENSION_DISPLAY.map((d) => {
      const match = dims.find((item) => item.dimension === d.key) || {};
      return {
        label: d.label,
        legendLabel: d.legendLabel,
        color: d.color,
        percent: Number(match.percent || 0),
        count: Number(match.count || 0),
        members: Number(match.members || 0),
      };
    });
  }
};
const fetchEconomicSecurity = async () => {
  const res = await apiGet("dashboard/overall/economicSecurity", { group_by: incomeTab.value ? incomeTab.value : null });
  if (res?.status === 200) {
    const data = res.data || {};
    const overall = data.overall || {};

    const stats = [
      { label: "รายได้เฉลี่ย", value: globalNumDecFixed(overall.avg_income || 0, 2), unit: `บาท/${props.scope}`, icon: "CircleDollarSignBgIcon" },
      { label: "เงินออมเฉลี่ย", value: globalNumDecFixed(overall.avg_saving || 0), unit: `บาท/${props.scope}`, icon: "SavingIcon" },
      { label: "หนี้สินเฉลี่ย", value: globalNumDecFixed(overall.avg_debt || 0), unit: `บาท/${props.scope}`, icon: "DeptIcon" },
      { label: "มีทรัพย์สิน", value: globalNumDecFixed(props.scope == "คน" ? overall.has_asset_member || 0 : overall.has_asset_household || 0), unit: `${props.scope} (${formatPercent(overall.has_asset_household, overall.total_household)})`, icon: "PropertyIcon" },
    ];
    economicStats.value = stats;
    nationalEconomicStats.value = stats;

    const byIncome = Array.isArray(data.by_income) ? data.by_income : [];
    incomeData.value = {
      labels: byIncome.map((item) => item.name || ""),
      datasets: [
        {
          label: "รายได้เฉลี่ย", backgroundColor: "#4e708c",
          data: byIncome.map((item) => Number(item.avg_income || 0)),
          barPercentage: 0.6, categoryPercentage: 0.8, yAxisID: "y",
        },
        {
          type: "line", label: "เส้นความยากจน", borderColor: "#3A506A", borderDash: [6, 6],
          borderWidth: 0, pointRadius: 0, hitRadius: 0, pointStyle: "line", yAxisID: "y",
          data: byIncome.map(() => 3078),
        },
      ],
    };
  }
};
const fetchEducation = async () => {
  const res = await apiGet("dashboard/overall/education", { group_by: eduTab.value !== 'not_country' ? eduTab.value : null });
  if (res?.status === 200) {
    const data = Array.isArray(res.data) ? res.data : [];
    eduData.value = {
      labels: data.map((item) => item.name || item.sector || item.age_group || ""),
      datasets: [
        {
          label: "จำนวน", backgroundColor: "#22ac6b",
          data: data.map((item) => Number(item.total || 0)),
          barPercentage: 0.6, categoryPercentage: 0.8,
        },
      ],
    };
  }
};
const fetchHealth = async () => {
  const res = await apiGet("dashboard/overall/health", { group_by: patientTab.value ? patientTab.value : null });
  if (res?.status === 200) {
    const data = res.data || {};
    const summary = data.summary || {};

    nationalHealthStats.value = [
      { label: "ไม่พิการ", value: Number(summary.not_disabled || 0), unit: props.scope, icon: "CircleXIcon" },
      { label: "พิการทางการเห็น", value: Number(summary.visual_impairment || 0), unit: props.scope, icon: "EyesIcon" },
      { label: "พิการทางการได้ยินหรือสื่อความหมาย", value: Number(summary.hearing_impairment || 0), unit: props.scope, icon: "EarsIcon" },
      { label: "พิการทางการเคลื่อนไหวหรือร่างกาย", value: Number(summary.physical_impairment || 0), unit: props.scope, icon: "DisabilityNewIcon" },
      { label: "พิการทางจิตใจหรือพฤติกรรม", value: Number(summary.mental_impairment || 0), unit: props.scope, icon: "MentallyIcon" },
      { label: "พิการทางการเรียนรู้", value: Number(summary.learning_disability || 0), unit: props.scope, icon: "BookOpenBgIcon" },
      { label: "ออทิสติก", value: Number(summary.autism || 0), unit: props.scope, icon: "AutismIcon" },
      { label: "พิการมากกว่า 1 ประเภท", value: Number(summary.multiple_disabilities || 0), unit: props.scope, icon: "CirclePlusBgIcon" },
    ];

    const byDisabledType = Array.isArray(data.by_disabled_type) ? data.by_disabled_type : [];
    nationalPatientData.value = {
      labels: byDisabledType.map((item) => item.name || ""),
      datasets: [
        { label: "ผู้ป่วย", backgroundColor: "#e08a2e", data: byDisabledType.map((item) => Number(item.with_ill || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
        { label: "ผู้พิการ", backgroundColor: "#5b7fc7", data: byDisabledType.map((item) => Number(item.with_disabled || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
      ],
    };
    disabilityType.value = {
      labels: byDisabledType.map((item) => item.name || ""),
      series: byDisabledType.map((item) => Number(item.with_disabled || 0)),
    };

    const byIllType = Array.isArray(data.by_ill_type) ? data.by_ill_type : [];
    const diseaseDisplay = [
      { key: "d1", display: ["ไม่มีโรคประจำตัวหรือ", "ความผิดปกติร้ายแรงใดๆ"] },
      { key: "d2", display: ["มีโรคประจำตัว กลุ่มโรค", "ไม่ติดต่อเรื้อรัง (NCDs)"] },
      { key: "d3", display: ["มีโรคประจำตัว กลุ่มโรคเกี่ยวกับอวัยวะ", "และระบบการทำงานที่สำคัญ"] },
      { key: "d4", display: ["มีโรคประจำตัว กลุ่มโรคหลอดเลือดสมอง", "ระบบประสาท/กล้ามเนื้อ และภาวะติดเชื้อ"] },
      { key: "d5", display: ["มีโรคประจำตัว กลุ่มการบาดเจ็บ", "ร้ายแรงและภาวะทุพพลภาพ"] },
      { key: "d6", display: ["มีอาการด้วยโรคจิตเวช"] }
    ]
    const diseaseMap = {
      "ไม่มีโรคประจำตัวหรือความผิดปกติร้ายแรงใดๆ": "d1",
      "มีโรคประจำตัว กลุ่มโรคไม่ติดต่อเรื้อรัง (NCDs)": "d2",
      "มีโรคประจำตัว กลุ่มโรคเกี่ยวกับอวัยวะและระบบการทำงานที่สำคัญ": "d3",
      "มีโรคประจำตัว กลุ่มโรคหลอดเลือดสมอง ระบบประสาท/กล้ามเนื้อ และภาวะติดเชื้อ": "d4",
      "มีโรคประจำตัว กลุ่มการบาดเจ็บร้ายแรงและภาวะทุพพลภาพ": "d5",
      "มีอาการด้วยโรคจิตเวช": "d6"
    }
    const diseaseLabels = diseaseDisplay.map((item) => item.display);
    const diseaseValues = diseaseDisplay.map((dp) =>
      Number(
        byIllType.find(
          (item) => diseaseMap[(item.name || "")] === dp.key
        )?.count || 0
      )
    );
    nationalDiseaseTypeData.value = {
      labels: diseaseLabels,
      datasets: [{ label: "จำนวน", backgroundColor: "#d6b170", data: diseaseValues, barPercentage: 0.6, categoryPercentage: 0.8 }],
    };
    // patientData.value = {
    //   labels: diseaseLabels,
    //   datasets: [{ label: "ผู้ป่วย", backgroundColor: "#F0BD2F", data: diseaseValues, barPercentage: 0.8, categoryPercentage: 0.7 }],
    // };
  }
};
const fetchQualityOfLife = async () => {
  const res = await apiGet("dashboard/overall/qualityOfLife", { group_by: qualityGroupTab.value ? qualityGroupTab.value : null });
  if (res?.status === 200) {
    const data = res.data || {};
    const summary = data.summary || {};

    nationalQualityStats.value = [
      { label: "มีน้ำกิน", value: Number(summary.has_water_drink || 0), unit: props.scope, icon: "DrinkingWaterIcon", percent: formatPercent(summary.has_water_drink, summary.total_household) },
      { label: "มีน้ำใช้", value: Number(summary.has_water_used || 0), unit: props.scope, icon: "WaterForUseIcon", percent: formatPercent(summary.has_water_used, summary.total_household) },
      { label: "มีไฟฟ้าใช้", value: Number(summary.has_electricity || 0), unit: props.scope, icon: "ElectricityIcon", percent: formatPercent(summary.has_electricity, summary.total_household) },
      { label: "เข้าถึงอินเทอร์เน็ต", value: Number(summary.has_internet || 0), unit: props.scope, icon: "InternetIcon", percent: formatPercent(summary.has_internet, summary.total_household) },
    ];


    const housingStability = data.housing_stability || {};
    nationalQualityDoughnut.value = {
      labels: ["มั่นคง", "ไม่มั่นคง"],
      series: [Number(housingStability.stable || 0), Number(housingStability.unstable || 0)],
    };

    const by_drink_water = data.by_water.map(({ id, name, has_water_drink, no_water_drink }) => ({ id, name, has_water_drink, no_water_drink }));
    const by_utility_water = data.by_water.map(({ id, name, has_water_used, no_water_used }) => ({ id, name, has_water_used, no_water_used }));

    qualityByTab.value = {
      home: Array.isArray(data.by_house_stability) ? data.by_house_stability : [],
      drink_water: Array.isArray(by_drink_water) ? by_drink_water : [],
      utility_water: Array.isArray(by_utility_water) ? by_utility_water : [],
      power: Array.isArray(data.by_electricity) ? data.by_electricity : [],
      internet: Array.isArray(data.by_internet) ? data.by_internet : [],
    };
  }
};
const fetchWelfareInformation = async () => {
  const res = await apiGet("dashboard/overall/welfareInformation", { group_by: serviceTab.value ? serviceTab.value : null });
  if (res?.status === 200) {
    const data = res.data || {};

    const byServiceAccess = Array.isArray(data.by_service_access) ? data.by_service_access : [];
    serviceData.value = {
      labels: byServiceAccess.map((item) => item.name || ""),
      datasets: [
        { label: "เข้าถึงบริการภาครัฐ", backgroundColor: "#2e8b6b", data: byServiceAccess.map((item) => Number(item.access_govt || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
        { label: "ได้รับการช่วยเหลือจากภาคส่วนอื่น", backgroundColor: "#637d87", data: byServiceAccess.map((item) => Number(item.received_other || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
      ],
    };

    const welfareByType = Array.isArray(data.welfare_by_type) ? data.welfare_by_type : [];
    const welfareDisplay = [
      { key: "d1", display: ["เบี้ยความพิการ"] },
      { key: "d2", display: ["เงินอุดหนุนเพื่อการเลี้ยงดู", "เด็กแรกเกิด"] },
      { key: "d3", display: ["เบี้ยยังชีพผู้สูงอายุ"] },
      { key: "d4", display: ["บัตรสวัสดิการแห่งรัฐ"] },
      { key: "d5", display: ["โครงการคนละครึ่ง"] },
      { key: "d6", display: ["โครงการเราชนะ"] },
      { key: "d7", display: ["ทุนการศึกษาแบบไม่ต้องใช้คืน"] },
      { key: "d8", display: ["โครงการเรารักกัน"] },
      { key: "d9", display: ["เงินช่วยเหลือจาก", "กองทุนคุ้มครองเด็ก"] },
      { key: "d10", display: ["โครงการยิ่งใช้ยิ่งได้"] },
      { key: "d11", display: ["เงินสงเคราะห์เบี้ยยังชีพ", "ผู้ป่วยเอดส์"] },
      { key: "other", display: ["สวัสดิการช่วยเหลืออื่น ๆ"] }
    ]
    const welfareMap = {
      "เบี้ยความพิการ": "d1",
      "เงินอุดหนุนเพื่อการเลี้ยงดูเด็กแรกเกิด": "d2",
      "เบี้ยยังชีพผู้สูงอายุ": "d3",
      "บัตรสวัสดิการแห่งรัฐ": "d4",
      "โครงการคนละครึ่ง": "d5",
      "โครงการเราชนะ": "d6",
      "ทุนการศึกษาแบบไม่ต้องใช้คืน (รวมจากทุกหน่วยงาน อาทิ โรงเรียน กองทุนเพื่อความเสมอภาคทางการศึกษาและ/หรือองค์กรปกครองส่วนท้องถิ่น": "d7",
      "โครงการเรารักกัน": "d8",
      "เงินช่วยเหลือจากกองทุนคุ้มครองเด็ก": "d9",
      "โครงการยิ่งใช้ยิ่งได้": "d10",
      "เงินสงเคราะห์เบี้ยยังชีพผู้ป่วยเอดส์": "d11",
      "สวัสดิการช่วยเหลืออื่น ๆ": "other"
    }
    const welfareLabels = welfareDisplay.map((item) => item.display);
    const welfareValues = welfareDisplay.map((dp) =>
      Number(
        welfareByType.find(
          (item) => welfareMap[(item.name || "")] === dp.key
        )?.total || 0
      )
    );
    welfareData.value = {
      labels: welfareLabels,
      datasets: [
        { label: "จำนวน", backgroundColor: "#1f6f8b", data: welfareValues, barPercentage: 0.6, categoryPercentage: 0.8 },
      ],
    };
  }
};

const fetchAll = () => {
  fetchOverall();
  fetchEconomicSecurity();
  fetchEducation();
  fetchHealth();
  fetchQualityOfLife();
  fetchWelfareInformation();
};

onMounted(fetchAll);
watch(filterParams, fetchAll, { deep: true, flush: 'post' });

const BasicStats = ref([])
const nationalTopProblems = ref([]);
const dimensionSummary = ref([]);

const nationalDimData = computed(() => ({
  labels: dimensionSummary.value.map((d) => d.label),
  datasets: [
    {
      label: "สภาพปัญหา",
      backgroundColor: "#3A506A",
      data: dimensionSummary.value.map((d) => d.percent),
      barPercentage: 0.6,
      categoryPercentage: 0.8,
    },
  ],
}));

const nationalDimOptions = {
  responsive: true,
  aspectRatio: 1.8,
  layout: { padding: { top: 20 } },
  plugins: {
    legend: { display: false },
    datalabels: {
      anchor: "end",
      align: "top",
      textAlign: "center",
      formatter: (value) => {
        return `${value}%`;
      },
      font: { size: 12, family: "Kanit" },
      color: "#374151",
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label(ctx) {
          const chartLabel = [].concat(ctx.chart.data.labels[ctx.dataIndex]).join('');
          const dim = dimensionSummary.value.find((d) => [].concat(d.label).join('') === chartLabel);
          const count = dim?.count ?? 0;
          return `${ctx.dataset.label}: ${ctx.parsed.y}% (${formatCount(count)} ${props.scope})`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, autoSkip: false } },
    y: {
      beginAtZero: true,
      max: 100,
      grid: { display: false, color: "#E2E8F0" },
      ticks: { font: { size: 12 }, callback: (v) => v + "%" },
    },
  },
};


const nationalHealthStats = ref([]);
const nationalPatientData = ref({ labels: [], datasets: [] });
const nationalDiseaseTypeData = ref({ labels: [], datasets: [] });


const nationalQualityStats = ref([]);
const nationalQualityDoughnut = ref({ labels: [], series: [] });
const HOUSING_STABILITY_COLOR_BY_LABEL = { "มั่นคง": "#3A506A", "ไม่มั่นคง": "#F73C4B" };
const nationalQualityDoughnutPalette = computed(() =>
  nationalQualityDoughnut.value.labels.map((label) => HOUSING_STABILITY_COLOR_BY_LABEL[label] || "#606468")
);

const qualityByTab = ref({ home: [], drink_water: [], utility_water: [], power: [], internet: [] });
const currentQualityRows = computed(() => qualityByTab.value[qualityTab.value] || []);

const nqChartWidth = computed(() => `${currentQualityRows.value.length * 60 + 200}px`);

const QUALITY_BAR_BUILDERS = {
  home: (rows) => [
    { label: "มั่นคง", backgroundColor: "#3A506A", data: rows.map((r) => Number(r.stable || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
    { label: "ไม่มั่นคง", backgroundColor: "#F73C4B", data: rows.map((r) => Number(r.unstable || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
  ],
  drink_water: (rows) => [
    { label: "มีน้ำกิน", backgroundColor: "#3A506A", data: rows.map((r) => Number(r.has_water_drink || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
    { label: "ไม่มีน้ำกิน", backgroundColor: "#F73C4B", data: rows.map((r) => Number(r.no_water_drink || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
  ],
  utility_water: (rows) => [
    { label: "มีน้ำใช้", backgroundColor: "#3A506A", data: rows.map((r) => Number(r.has_water_used || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
    { label: "ไม่มีน้ำใช้", backgroundColor: "#F73C4B", data: rows.map((r) => Number(r.no_water_used || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
  ],
  power: (rows) => [
    { label: "มีไฟฟ้าใช้", backgroundColor: "#3A506A", data: rows.map((r) => Number(r.has_electricity || 0)), barPercentage: 0.6, categoryPercentage: 0.8 },
    { label: "ไม่มีไฟฟ้าใช้", backgroundColor: "#F73C4B", data: rows.map((r) => Number(r.no_electricity || 0)), barPercentage: 0.8, categoryPercentage: 0.7 },
  ],
  internet: (rows) => [
    { label: "เข้าถึงอินเทอร์เน็ต", backgroundColor: "#3A506A", data: rows.map((r) => Number(r.has_internet || 0)), barPercentage: 0.6, categoryPercentage: 0.8 },
    { label: "เข้าไม่ถึงอินเทอร์เน็ต", backgroundColor: "#F73C4B", data: rows.map((r) => Number(r.no_internet || 0)), barPercentage: 0.6, categoryPercentage: 0.8 },
  ],
};

const nationalQualityBarData = computed(() => ({
  labels: currentQualityRows.value.map((r) => r.name || ""),
  datasets: QUALITY_BAR_BUILDERS[qualityTab.value](currentQualityRows.value),
}));

const nationalQualityBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 30, right: 20 } },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 45, minRotation: 45, autoSkip: false } },
    y: { beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } },
  },
};

const eduTabs = computed(() => {
  if(props.area === "ทั้งประเทศ"){
    return [
      { label: "รายจังหวัด", value: "province" },
      { label: "รายภาค", value: "sector" },
      { label: "ตามช่วงอายุ", value: "age_group" },
    ]
  }
  return [
    { label: props.area, value: "not_country" },
    { label: "ตามช่วงอายุ", value: "age_group" },
  ]
});
const eduTab = ref(props.area === "ทั้งประเทศ" ? "province" : "not_country");


const SECTOR_TABS = [
  { label: "รายจังหวัด", value: "province" },
  { label: "รายภาค", value: "sector" },
];

const incomeTabs = computed(() => props.area === "ทั้งประเทศ" ? SECTOR_TABS : []);
const incomeTab = ref(props.area === "ทั้งประเทศ" ? "province" : null);

const patientTabs = computed(() => props.area === "ทั้งประเทศ" ? SECTOR_TABS : []);
const patientTab = ref(props.area === "ทั้งประเทศ" ? "province" : null);

const qualityGroupTabs = computed(() => props.area === "ทั้งประเทศ" ? SECTOR_TABS : []);
const qualityGroupTab = ref(props.area === "ทั้งประเทศ" ? "province" : null);

const serviceTabs = computed(() => props.area === "ทั้งประเทศ" ? SECTOR_TABS : []);
const serviceTab = ref(props.area === "ทั้งประเทศ" ? "province" : null);

let areaUpdatingTabs = false;
watch(
  () => props.area,
  (val) => {
    areaUpdatingTabs = true;
    if (val === "ทั้งประเทศ") {
      eduTab.value = "province";
      incomeTab.value = "province";
      patientTab.value = "province";
      qualityGroupTab.value = "province";
      serviceTab.value = "province";
    } 
    else {
      eduTab.value = "not_country";
      incomeTab.value = null;
      patientTab.value = null;
      qualityGroupTab.value = null;
      serviceTab.value = null;
    }
    areaUpdatingTabs = false;
  }
);
watch(eduTab, () => { if (!areaUpdatingTabs) fetchEducation(); }, { flush: 'sync' });
watch(incomeTab, () => { if (!areaUpdatingTabs) fetchEconomicSecurity(); }, { flush: 'sync' });
watch(patientTab, () => { if (!areaUpdatingTabs) fetchHealth(); }, { flush: 'sync' });
watch(qualityGroupTab, () => { if (!areaUpdatingTabs) fetchQualityOfLife(); }, { flush: 'sync' });
watch(serviceTab, () => { if (!areaUpdatingTabs) fetchWelfareInformation(); }, { flush: 'sync' });

const qualityTabs = [
  { label: "สภาพของที่อยู่อาศัย", value: "home" },
  { label: "น้ำกิน", value: "drink_water" },
  { label: "น้ำใช้", value: "utility_water" },
  { label: "ไฟฟ้า", value: "power" },
  { label: "อินเทอร์เน็ต", value: "internet" },
];
const qualityTab = ref("home");

const economicStats = ref([]);
const nationalEconomicStats = ref([]);

// const qualityStats = ref([]);



const incomeData = ref({ labels: [], datasets: [] });


const average = (values) => (values.length ? values.reduce((a, b) => a + Number(b || 0), 0) / values.length : 0);

const createAverageLinePlugin = (id, thresholdRef, unitLabel) => ({
  id,
  afterDraw(chart) {
    const { ctx, scales: { y }, chartArea: { left, right } } = chart;
    if (!y) return;
    const yPx = y.getPixelForValue(thresholdRef.value);
    const headLen = 8, headHalf = 4;
    const arrowStart = right + 6, arrowEnd = right + 24;

    ctx.save();
    ctx.strokeStyle = "#232629";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(left, yPx);
    ctx.lineTo(right, yPx);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(arrowStart, yPx);
    ctx.lineTo(arrowEnd - headLen, yPx);
    ctx.stroke();

    ctx.fillStyle = "#232629";
    ctx.beginPath();
    ctx.moveTo(arrowEnd, yPx);
    ctx.lineTo(arrowEnd - headLen, yPx - headHalf);
    ctx.lineTo(arrowEnd - headLen, yPx + headHalf);
    ctx.closePath();
    ctx.fill();

    ctx.font = "12px Kanit, sans-serif";
    ctx.fillStyle = "#232629";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(`เฉลี่ย`, arrowEnd + 4, yPx - 14);
    ctx.fillText(formatCount(Math.round(thresholdRef.value)), arrowEnd + 4, yPx + 3);
    ctx.fillText(unitLabel, arrowEnd + 4, yPx + 18);
    ctx.restore();
  },
});

// const eduThreshold = computed(() => average(eduData.value.datasets[0]?.data || []));
// const eduArrowPlugin = createAverageLinePlugin("eduArrow", eduThreshold, props.scope);

const povertyLinePlugin = {
  id: "povertyLine",
  afterDraw(chart) {
    const idx = chart.data.datasets.findIndex((ds) => ds.type === "line");
    if (idx === -1 || !chart.isDatasetVisible(idx)) return;
    const ds = chart.data.datasets[idx];
    const value = ds.data?.[0];
    if (!value) return;
    const { ctx, scales: { y }, chartArea: { left, right } } = chart;
    if (!y) return;
    const yPx = y.getPixelForValue(value);
    const headLen = 8, headHalf = 4;
    const arrowStart = right + 6, arrowEnd = right + 24;
    ctx.save();
    ctx.strokeStyle = "#3A506A";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath(); ctx.moveTo(left, yPx); ctx.lineTo(right, yPx); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(arrowStart, yPx); ctx.lineTo(arrowEnd - headLen, yPx); ctx.stroke();
    ctx.fillStyle = "#3A506A";
    ctx.beginPath();
    ctx.moveTo(arrowEnd, yPx);
    ctx.lineTo(arrowEnd - headLen, yPx - headHalf);
    ctx.lineTo(arrowEnd - headLen, yPx + headHalf);
    ctx.closePath(); ctx.fill();
    ctx.font = "12px Kanit, sans-serif";
    ctx.fillStyle = "#3A506A";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(ds.label, arrowEnd + 4, yPx - 14);
    ctx.fillText(globalNumDecFixed(value), arrowEnd + 4, yPx + 3);
    ctx.fillText(`บาท`, arrowEnd + 4, yPx + 18);
    ctx.restore();
  },
};

// const qualityThreshold = computed(() => average(nationalQualityBarData.value.datasets.flatMap((ds) => ds.data)));
// const nationalQualityArrowPlugin = computed(()=> createAverageLinePlugin("nqArrow", qualityThreshold, props.scope));

const eduData = ref({ labels: [], datasets: [] });


const disabilityType = ref({ labels: [], series: [] });

// const patientData = ref({ labels: [], datasets: [] });


const qualityData = nationalQualityBarData;


const serviceData = ref({ labels: [], datasets: [] });
const welfareData = ref({ labels: [], datasets: [] });

const baseOptions = {
  responsive: true,
  aspectRatio: 3,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: { usePointStyle: false, boxWidth: 30, boxHeight: 8, font: { size: 12 } },
    },
    tooltip: { enabled: true },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 45, minRotation: 45, autoSkip: false } },
    y: { beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } },
  },
};

const simpleBarOptions = {
  ...baseOptions,
  aspectRatio: 1.6,
  plugins: { ...baseOptions.plugins, legend: { display: false } },
};

const groupedOptions = {
  ...baseOptions,
  aspectRatio: 2.4,
  plugins: {
    ...baseOptions.plugins,
    legend: {
      display: true,
      position: "bottom",
      labels: { usePointStyle: false, boxWidth: 30, boxHeight: 8, font: { size: 12 } },
    },
  },
};

const groupBarChartOptions = { ...groupedOptions, maintainAspectRatio: false };
const simpleBarChartOptions = { ...simpleBarOptions, maintainAspectRatio: false };

const thresholdOptions = { ...baseOptions, aspectRatio: 3, layout: { padding: { top: 30, right: 20 } } };

const dualAxisOptions = {
  ...baseOptions,
  aspectRatio: 3,
  layout: { padding: { right: 120 } },
  plugins: {
    ...baseOptions.plugins,
    legend: {
      display: true,
      position: "bottom",
      labels: {
        usePointStyle: true,
        boxWidth: 30,
        boxHeight: 8,
        font: { size: 12 },
        generateLabels(chart) {
          return chart.data.datasets.map((ds, i) => ({
            text: ds.type === "line"
                ? `${ds.label} (${globalNumDecFixed(3078)} บาท)`
                : ds.label,
            datasetIndex: i,
            hidden: !chart.isDatasetVisible(i),
            fillStyle: ds.type === "line" ? "transparent" : ds.backgroundColor,
            strokeStyle: ds.borderColor || ds.backgroundColor,
            lineWidth: ds.type === "line" ? (ds.borderWidth || 1.5) : 0,
            lineDash: ds.borderDash || [],
            pointStyle: ds.type === "line" ? "line" : "rect",
          }));
        },
      },
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label(ctx) {
          const val = globalNumDecFixed(ctx.parsed.y);
          return `${ctx.dataset.label}: ${val} บาท/${props.scope}`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 45, minRotation: 45, autoSkip: false } },
    y: { position: "left", beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 }, title: { display: true, text: "บาท", font: { size: 12 } } },
  },
};
</script>
